import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { datadogRum } from '@datadog/browser-rum';
import { css } from '@emotion/css';
import {
  PropertyTitleOwnerOutput,
  useSubmitTitleSearchOwnerMutation,
  useUpdateTitleSearchOwnerMutation,
} from '@graphql/index';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Heading } from '@spark-web/heading';
import { Stack } from '@spark-web/stack';
import EligibilityPageHeading from './EligibilityPageHeading';
import EligibilityStepHeader from './EligibilityStepHeader';
import OwnerVerificationCard from './OwnerVerificationCard';
import { INCOME_TYPES, type IncomeType } from './incomeTypes';
import type { OwnerVerificationStatus, VerifyIncomeFormValues } from './verifyIncomeStatus';

type VerifyDeclaredIncomeProps = {
  owners: PropertyTitleOwnerOutput[];
  declaredIncomes: Record<string, string>;
  incomeTypes: Record<string, string>;
  trackingId: string;
  productName: string;
  applicantName: string;
  onConfirm: () => void;
  onBack: () => void;
};

const AVATAR_BACKGROUNDS: Array<'primary' | 'secondary'> = ['primary', 'secondary'];

function VerifyDeclaredIncome({
  owners,
  declaredIncomes,
  incomeTypes,
  trackingId,
  productName,
  applicantName,
  onConfirm,
  onBack,
}: VerifyDeclaredIncomeProps) {
  const [ownerStatuses, setOwnerStatuses] = useState<Record<string, OwnerVerificationStatus>>({});
  // IDMatrix reference per owner from a successful pensioner ACCEPT — threaded into submit so
  // the backend marks that owner verified.
  const [ownerIdMatrixReferences, setOwnerIdMatrixReferences] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingOwner, setIsSavingOwner] = useState(false);
  const [currentOwnerIndex, setCurrentOwnerIndex] = useState(0);

  // react-hook-form owns the per-owner field inputs (consent, document choice, concession card,
  // $0 self-declaration). The async upload/verify lifecycle stays in each card's local state,
  // and its terminal state is bubbled back up via onStatusChange for the submit payload + gating.
  const { control, watch } = useForm<VerifyIncomeFormValues>({
    mode: 'all',
    defaultValues: {
      consents: Object.fromEntries(owners.map(owner => [owner.id, false])),
      zeroIncomeConfirmed: Object.fromEntries(owners.map(owner => [owner.id, false])),
      documentChoice: Object.fromEntries(owners.map(owner => [owner.id, 'PRIMARY'])),
      concessionCard: Object.fromEntries(owners.map(owner => [owner.id, { name: '', expiry: undefined, crn: '' }])),
    },
  });

  const { mutateAsync: submitIncome } = useSubmitTitleSearchOwnerMutation({ useErrorBoundary: false });
  const { mutateAsync: saveOwnerOnAdvance } = useUpdateTitleSearchOwnerMutation({ useErrorBoundary: false });

  const handleStatusChange = useCallback((ownerId: string, ownerStatus: OwnerVerificationStatus) => {
    setOwnerStatuses(prev => ({ ...prev, [ownerId]: ownerStatus }));
  }, []);

  const handleIdMatrixReference = useCallback((ownerId: string, idMatrixReference: string | null) => {
    setOwnerIdMatrixReferences(prev => {
      if (idMatrixReference === null) {
        if (!(ownerId in prev)) return prev;
        const next = { ...prev };
        delete next[ownerId];
        return next;
      }
      return { ...prev, [ownerId]: idMatrixReference };
    });
  }, []);

  const consents = watch('consents') ?? {};

  // Statuses that complete an owner without a consent checkbox. 'provided' covers the
  // non-NOA document forms (Pensioner Concession Card / Superannuation statement), which
  // have no consent checkbox — they're ready once their inputs are supplied.
  const isConsentExemptStatus = (ownerStatus?: OwnerVerificationStatus) =>
    ownerStatus === 'failed_final' || ownerStatus === 'zero_income_skip' || ownerStatus === 'provided';
  // Statuses that count as "finished" for this owner. A passed NOA and a $0 NOA
  // (income_zero_noa) complete via the consent checkbox; the exempt statuses complete
  // on their own.
  const isDoneStatus = (ownerStatus?: OwnerVerificationStatus) =>
    ownerStatus === 'passed' || ownerStatus === 'income_zero_noa' || isConsentExemptStatus(ownerStatus);

  // Single source of truth for an owner's income verdict, shared by the per-owner
  // save-on-advance (updateTitleSearchOwner) and the final submit so the two calls
  // can never disagree. An owner is unverified when its terminal status is a manual-
  // review outcome (failed_final / zero_income_skip / a non-NOA document via 'provided').
  //
  // A $0 NOA (income_zero_noa) always stays verified, regardless of income type — we never
  // send isIncomeVerified:false for it, so the BE derives the verdict from the persisted
  // incomeType (e.g. the pensioner short-circuit for a declared pensioner).
  //
  // Pensioner Concession Card is the one exception to the "'provided' ⇒ unverified"
  // rule: a card verify resolves to 'provided' for BOTH an IDMatrix ACCEPT and the
  // NOT-ACCEPT/retry-cap fallback. The two are told apart by the IDMatrix reference —
  // an ACCEPT carries one (verified), a NOT-ACCEPT or cap-reached outcome clears it
  // (manual review ⇒ unverified). So a pensioner is unverified only when their card
  // did not ACCEPT (no reference).
  const isOwnerIncomeUnverified = (owner: PropertyTitleOwnerOutput) => {
    const ownerStatus = ownerStatuses[owner.id];
    const isPensioner = incomeTypes[owner.id] === INCOME_TYPES.PENSIONER;
    const isAcceptedPensionerCard = isPensioner && ownerStatus === 'provided' && !!ownerIdMatrixReferences[owner.id];
    return (
      ownerStatus === 'failed_final' ||
      ownerStatus === 'zero_income_skip' ||
      (ownerStatus === 'provided' && !isAcceptedPensionerCard)
    );
  };

  const isOwnerConsented = (owner: PropertyTitleOwnerOutput) =>
    isConsentExemptStatus(ownerStatuses[owner.id]) || consents[owner.id] === true;

  // Every owner is verified sequentially and reaches its own terminal state — a
  // failed_final owner no longer short-circuits the batch. "Continue to application"
  // unlocks only once ALL owners are done (and every consent-required owner consented).
  const allConsented = owners.every(isOwnerConsented);
  const allOwnersDone = owners.every(owner => isDoneStatus(ownerStatuses[owner.id]));
  const canContinue = allConsented && allOwnersDone;

  const isOwnerReady = useCallback(
    (owner: PropertyTitleOwnerOutput) => {
      const ownerStatus = ownerStatuses[owner.id];
      return isDoneStatus(ownerStatus) && (isConsentExemptStatus(ownerStatus) || consents[owner.id] === true);
    },
    [ownerStatuses, consents],
  );

  const currentOwner = owners[currentOwnerIndex];
  const isLastOwner = currentOwnerIndex === owners.length - 1;
  // A failed_final owner is "ready" (terminal, consent-exempt), so the wizard advances
  // to the next owner instead of skipping ahead. "Continue to application" shows only on
  // the last owner — i.e. once every owner has been through their verification path.
  const showContinueButton = isLastOwner;
  const canGoToNextOwner = !!currentOwner && isOwnerReady(currentOwner);

  const handleBack = () => {
    if (currentOwnerIndex > 0) {
      setCurrentOwnerIndex(index => index - 1);
    } else {
      onBack();
    }
  };

  // Per-owner save-and-continue: persist the current owner before advancing the
  // wizard on EVERY step so a confirmed declaration survives a drop-off. The input
  // mirrors the submit payload exactly — a NO_INCOME owner's "$0 / no NOA" flag, a
  // PENSIONER's IDMatrix reference from a card ACCEPT, and isIncomeVerified:false ONLY
  // when the shared verdict is unverified (omitted otherwise, so the BE derives the
  // verified/pensioner verdict — a confirmed pensioner must never be sent an explicit
  // verdict). declaredIncome is stored monthly (BE convention), matching the
  // declare-step save and the submit payload.
  const handleNextOwner = async () => {
    const advance = () => setCurrentOwnerIndex(index => index + 1);
    if (!currentOwner) {
      advance();
      return;
    }

    const ownerIncomeType = incomeTypes[currentOwner.id];
    const idMatrixReference = ownerIdMatrixReferences[currentOwner.id];
    const isNoIncome = ownerIncomeType === INCOME_TYPES.NO_INCOME;
    const ownerInput = {
      id: currentOwner.id,
      declaredIncome: isNoIncome ? 0 : Number(declaredIncomes[currentOwner.id] ?? 0) / 12,
      incomeType: ownerIncomeType as IncomeType,
      ...(isNoIncome ? { hasNoIncome: true } : {}),
      ...(idMatrixReference ? { idMatrixReference } : {}),
      ...(isOwnerIncomeUnverified(currentOwner) ? { isIncomeVerified: false } : {}),
    };

    setIsSavingOwner(true);
    try {
      await saveOwnerOnAdvance({ input: { applicationId: trackingId, owners: [ownerInput] } });
    } catch {
      datadogRum.addAction('LandOwner Advance Save Error', { trackingId, ownerId: currentOwner.id });
    }
    setIsSavingOwner(false);
    advance();
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await submitIncome({
        input: {
          applicationId: trackingId,
          owners: owners.map(owner => {
            const annualIncome = Number(declaredIncomes[owner.id] ?? 0);
            const monthlyIncome = annualIncome / 12;
            // A pensioner whose card verify returned ACCEPT carries an IDMatrix reference. We send
            // the reference — the backend validates it against the persisted ACCEPT and is the
            // source of truth for the verdict.
            const idMatrixReference = ownerIdMatrixReferences[owner.id];
            // Each owner's verdict is its own terminal status (owners are verified sequentially
            // and none short-circuits the others). A confirmed pensioner must NOT send
            // isIncomeVerified:false — the BE treats an explicit false as an override that skips
            // the pensioner short-circuit — so we only send the field when unverified and let the
            // BE derive the verified/pensioner verdict from the persisted incomeType otherwise.
            const isUnverified = isOwnerIncomeUnverified(owner);
            return {
              id: owner.id,
              declaredIncome: monthlyIncome,
              ...(idMatrixReference ? { idMatrixReference } : {}),
              ...(isUnverified ? { isIncomeVerified: false } : {}),
            };
          }),
        },
      });
      onConfirm();
    } catch {
      datadogRum.addError(new Error('HESL income submission failed'), {
        trackingId,
        ownerCount: owners.length,
      });
      datadogRum.addAction('LandOwner Income Submit Error', { trackingId, ownerCount: owners.length });
      onConfirm();
    }
    setIsSubmitting(false);
  };

  return (
    <Stack padding={{ tablet: 'xlarge' }} gap="xxlarge">
      <EligibilityStepHeader currentStep={5} totalSteps={5} />

      <EligibilityPageHeading productName={productName}>
        <Box className={css({ lineHeight: '1.3em' })}>
          <Heading level="1">Verify Income for {currentOwner?.fullName ?? ''}</Heading>
        </Box>
      </EligibilityPageHeading>

      <Stack gap="xlarge">
        {owners.map((owner, index) => (
          <Box key={owner.id} className={index === currentOwnerIndex ? undefined : css({ display: 'none' })}>
            <OwnerVerificationCard
              owner={owner}
              declaredIncome={declaredIncomes[owner.id] ?? '0'}
              incomeType={incomeTypes[owner.id]}
              trackingId={trackingId}
              avatarBackground={AVATAR_BACKGROUNDS[index] ?? 'secondary'}
              isApplicant={owner.fullName.trim().toLowerCase() === applicantName.trim().toLowerCase()}
              control={control}
              watch={watch}
              onStatusChange={handleStatusChange}
              onIdMatrixReference={handleIdMatrixReference}
            />
          </Box>
        ))}

        <Stack gap="large">
          {showContinueButton ? (
            <Button tone="primary" onClick={handleConfirm} disabled={!canContinue} loading={isSubmitting}>
              Continue to application
            </Button>
          ) : (
            <Button tone="primary" onClick={handleNextOwner} disabled={!canGoToNextOwner} loading={isSavingOwner}>
              Continue
            </Button>
          )}
          <Button prominence="none" tone="neutral" onClick={handleBack}>
            Back
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default VerifyDeclaredIncome;
